import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, ArrowUpRight, Upload, X } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";

const PORTION_INFO =
    "Dessert should be chosen if you have no other sweets being served at your event and usually cut approx 2 x 1 x 4 inches, otherwise if desserts are being served coffee portions will be enough, usually cut at 1 x 1 x 4 inches finger slices.";

export default function RectangularCakeInquiryForm({ product }) {
    const sizeOptions = product?.size_options || [];
    const [form, setForm] = useState({
        event_date: "",
        name: "",
        phone: "",
        venue: "",
        message: "",
        flavour_choice: "",
    });
    const [sizeLabel, setSizeLabel] = useState("");
    const [fulfillment, setFulfillment] = useState("");
    const [inspirationUrl, setInspirationUrl] = useState("");
    const [uploading, setUploading] = useState(false);
    const [portionInfoOpen, setPortionInfoOpen] = useState(false);
    const [dimensionsOpen, setDimensionsOpen] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const fileInputRef = useRef(null);

    const selectedSize = sizeOptions.find((s) => s.label === sizeLabel);

    const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

    const uploadFile = async (file) => {
        if (!file) return;
        if (!file.type.startsWith("image/") && file.type !== "application/pdf") {
            toast.error("Please upload an image or PDF.");
            return;
        }
        if (file.size > 8 * 1024 * 1024) {
            toast.error("File must be under 8 MB.");
            return;
        }
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const r = await api.post("/inquiry-upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
            setInspirationUrl(r.data.url);
            toast.success("Design uploaded");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Upload failed");
        } finally {
            setUploading(false);
        }
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        if (submitting) return;
        if (!sizeLabel) { toast.error("Please choose a cake size."); return; }
        if (!fulfillment) { toast.error("Please choose pick up or delivery."); return; }
        setSubmitting(true);
        try {
            await api.post("/inquiries", {
                ...form,
                product_slug: product?.slug,
                product_name: product?.name,
                size_label: sizeLabel,
                size_price: selectedSize?.price ?? null,
                fulfillment_type: fulfillment,
                inspiration_url: inspirationUrl || null,
            });
            toast.success("Thank you! Your enquiry has been sent. We'll be in touch shortly.");
            setForm({ event_date: "", name: "", phone: "", venue: "", message: "", flavour_choice: "" });
            setSizeLabel("");
            setFulfillment("");
            setInspirationUrl("");
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <form onSubmit={onSubmit} className="space-y-5" data-testid="rect-inquiry-form">
            <div>
                <div className="flex items-center justify-between mb-2">
                    <label className="block text-[10px] tracking-[0.25em] uppercase text-ink-soft">Flavour</label>
                    <Link
                        to="/flavours"
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1 text-[10px] tracking-[0.2em] uppercase text-blush hover:text-[#C4007A] transition-colors shrink-0"
                        data-testid="rect-flavour-list-link"
                    >
                        View flavours list <ArrowUpRight size={11} />
                    </Link>
                </div>
                <input
                    type="text"
                    name="flavour_choice"
                    value={form.flavour_choice}
                    onChange={onChange}
                    placeholder="e.g. Vanilla, Chocolate…"
                    className="w-full px-4 py-3 bg-transparent border border-[#EAE3DE] focus:outline-none focus:border-blush text-ink"
                    data-testid="rect-flavour-choice"
                />
            </div>

            <div>
                <label className="block text-[10px] tracking-[0.25em] uppercase text-ink-soft mb-2">Cake Size</label>
                <select
                    required
                    value={sizeLabel}
                    onChange={(e) => setSizeLabel(e.target.value)}
                    className="w-full px-4 py-3 bg-transparent border border-[#EAE3DE] focus:outline-none focus:border-blush text-ink"
                    data-testid="rect-size"
                >
                    <option value="">Select a size…</option>
                    {sizeOptions.map((s) => (
                        <option key={s.label} value={s.label}>
                            {s.label} ({s.dessert_serves} Dessert, {s.coffee_serves} Coffee)
                        </option>
                    ))}
                </select>
                {selectedSize && (
                    <p className="text-blush text-lg font-medium mt-2" data-testid="rect-size-price">
                        ${Number(selectedSize.price).toFixed(0)}
                    </p>
                )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                    <label className="block text-[10px] tracking-[0.25em] uppercase text-ink-soft mb-2">Date</label>
                    <input
                        type="date"
                        name="event_date"
                        required
                        value={form.event_date}
                        onChange={onChange}
                        className="w-full px-4 py-3 bg-transparent border border-[#EAE3DE] focus:outline-none focus:border-blush text-ink"
                        data-testid="rect-date"
                    />
                </div>
                <div>
                    <label className="block text-[10px] tracking-[0.25em] uppercase text-ink-soft mb-2">Name</label>
                    <input
                        type="text"
                        name="name"
                        required
                        value={form.name}
                        onChange={onChange}
                        className="w-full px-4 py-3 bg-transparent border border-[#EAE3DE] focus:outline-none focus:border-blush text-ink"
                        data-testid="rect-name"
                    />
                </div>
            </div>

            <div>
                <label className="block text-[10px] tracking-[0.25em] uppercase text-ink-soft mb-2">Number</label>
                <input
                    type="tel"
                    name="phone"
                    required
                    value={form.phone}
                    onChange={onChange}
                    className="w-full px-4 py-3 bg-transparent border border-[#EAE3DE] focus:outline-none focus:border-blush text-ink"
                    data-testid="rect-phone"
                />
            </div>

            <div>
                <label className="block text-[10px] tracking-[0.25em] uppercase text-ink-soft mb-2">Pick up or delivery</label>
                <div className="flex items-center gap-6">
                    <label className="flex items-center gap-2 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={fulfillment === "Pickup"}
                            onChange={() => setFulfillment(fulfillment === "Pickup" ? "" : "Pickup")}
                            data-testid="rect-pickup"
                        />
                        <span className="text-sm text-ink">Pick up</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={fulfillment === "Delivery"}
                            onChange={() => setFulfillment(fulfillment === "Delivery" ? "" : "Delivery")}
                            data-testid="rect-delivery"
                        />
                        <span className="text-sm text-ink">Delivery</span>
                    </label>
                </div>
            </div>

            <div>
                <label className="block text-[10px] tracking-[0.25em] uppercase text-ink-soft mb-2">Location of venue</label>
                <input
                    type="text"
                    name="venue"
                    value={form.venue}
                    onChange={onChange}
                    className="w-full px-4 py-3 bg-transparent border border-[#EAE3DE] focus:outline-none focus:border-blush text-ink"
                    data-testid="rect-venue"
                />
            </div>

            <div>
                <label className="block text-[10px] tracking-[0.25em] uppercase text-ink-soft mb-2">Message</label>
                <textarea
                    name="message"
                    rows={3}
                    value={form.message}
                    onChange={onChange}
                    className="w-full px-4 py-3 bg-transparent border border-[#EAE3DE] focus:outline-none focus:border-blush text-ink resize-none"
                    data-testid="rect-message"
                />
            </div>

            <div>
                <label className="block text-[10px] tracking-[0.25em] uppercase text-ink-soft mb-2">
                    Have a custom design in mind? Upload a pic
                </label>
                {inspirationUrl ? (
                    <div className="relative inline-block">
                        <img src={inspirationUrl} alt="Design inspiration" className="w-32 h-32 object-cover border border-[#EAE3DE]" />
                        <button
                            type="button"
                            onClick={() => setInspirationUrl("")}
                            className="absolute -top-2 -right-2 bg-white border border-[#EAE3DE] rounded-full p-1 hover:bg-blush hover:text-white"
                            aria-label="Remove image"
                        >
                            <X size={12} />
                        </button>
                    </div>
                ) : (
                    <div
                        onClick={() => fileInputRef.current?.click()}
                        className="border-2 border-dashed border-[#EAE3DE] hover:border-blush p-6 text-center cursor-pointer transition-colors"
                        data-testid="rect-dropzone"
                    >
                        {uploading ? (
                            <p className="text-sm text-ink-soft">Uploading…</p>
                        ) : (
                            <>
                                <Upload size={22} className="mx-auto mb-2 text-blush" />
                                <p className="text-xs text-ink-soft">Click to upload a design image</p>
                            </>
                        )}
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/*,application/pdf"
                            onChange={(e) => uploadFile(e.target.files?.[0])}
                            className="hidden"
                            data-testid="rect-file-input"
                        />
                    </div>
                )}
            </div>

            <div className="border-t border-[#EAE3DE] pt-4">
                <button
                    type="button"
                    onClick={() => setPortionInfoOpen((v) => !v)}
                    className="flex items-center justify-between w-full text-left"
                    data-testid="rect-portion-info-toggle"
                >
                    <span className="text-[11px] tracking-[0.25em] uppercase text-ink font-medium">Do I choose Dessert or coffee?</span>
                    <span className="w-7 h-7 border border-ink flex items-center justify-center shrink-0">
                        <ChevronDown size={14} className={`transition-transform ${portionInfoOpen ? "rotate-180" : ""}`} />
                    </span>
                </button>
                {portionInfoOpen && (
                    <p className="mt-4 text-xs text-ink-soft leading-relaxed" data-testid="rect-portion-info-content">
                        {PORTION_INFO}
                    </p>
                )}
            </div>

            <div className="border-t border-[#EAE3DE] pt-4">
                <button
                    type="button"
                    onClick={() => setDimensionsOpen((v) => !v)}
                    className="flex items-center justify-between w-full text-left"
                    data-testid="rect-dimensions-toggle"
                >
                    <span className="text-[11px] tracking-[0.25em] uppercase text-ink font-medium">Size and Dimensions</span>
                    <span className="w-7 h-7 border border-ink flex items-center justify-center shrink-0">
                        <ChevronDown size={14} className={`transition-transform ${dimensionsOpen ? "rotate-180" : ""}`} />
                    </span>
                </button>
                {dimensionsOpen && (
                    <div className="mt-4 space-y-2" data-testid="rect-dimensions-content">
                        {sizeOptions.map((s) => (
                            <p key={s.label} className="text-xs text-ink-soft leading-relaxed">
                                <span className="text-ink font-medium">{s.label}</span> — {s.dimensions} — {s.dessert_serves} dessert / {s.coffee_serves} coffee
                            </p>
                        ))}
                    </div>
                )}
            </div>

            <button
                type="submit"
                disabled={submitting}
                className="btn-pink w-full disabled:opacity-60"
                data-testid="rect-inquiry-submit"
            >
                {submitting ? "Sending…" : "Send Enquiry"}
            </button>
        </form>
    );
}
